import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, FileCheck, Building2, UtensilsCrossed, ShoppingBag, Coffee, Check } from 'lucide-react';

type ReceiptTypeOption = 'boleta' | 'boleta-dni' | 'factura';
type DiningOption = 'salon' | 'para-llevar' | 'ir-comiendo';

interface ReceiptTypeSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: ReceiptTypeOption, diningOption: DiningOption) => void;
}

const receiptTypes = [
  { value: 'boleta' as ReceiptTypeOption, label: 'Boleta', icon: FileText, color: 'green' },
  { value: 'boleta-dni' as ReceiptTypeOption, label: 'Boleta c/DNI', icon: FileCheck, color: 'blue' },
  { value: 'factura' as ReceiptTypeOption, label: 'Factura', icon: Building2, color: 'orange' },
];

const diningOptions = [
  { value: 'salon' as DiningOption, label: 'Salón', icon: UtensilsCrossed, color: 'purple' },
  { value: 'para-llevar' as DiningOption, label: 'Llevar', icon: ShoppingBag, color: 'cyan' },
  { value: 'ir-comiendo' as DiningOption, label: 'Ir Comiendo', icon: Coffee, color: 'amber' },
];

export const ReceiptTypeSelector: React.FC<ReceiptTypeSelectorProps> = ({
  isOpen,
  onClose,
  onSelect
}) => {
  const [selectedReceiptType, setSelectedReceiptType] = useState<ReceiptTypeOption | null>(null);
  const [selectedDiningOption, setSelectedDiningOption] = useState<DiningOption | null>(null);

  const handleClose = () => {
    setSelectedReceiptType(null);
    setSelectedDiningOption(null);
    onClose();
  };

  // Auto-continuar cuando ambas opciones están seleccionadas
  useEffect(() => {
    if (selectedReceiptType && selectedDiningOption) {
      onSelect(selectedReceiptType, selectedDiningOption);
      setSelectedReceiptType(null);
      setSelectedDiningOption(null);
    }
  }, [selectedReceiptType, selectedDiningOption, onSelect]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-900 text-center">
            Información de la Venta
          </DialogTitle>
        </DialogHeader>

        {/* Sección 1: Tipo de Comprobante */}
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-gray-700 border-b pb-2">
            1. Tipo de Comprobante
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {receiptTypes.map(({ value, label, icon: Icon, color }) => {
              const selected = selectedReceiptType === value;
              return (
                <Card
                  key={value}
                  onClick={() => setSelectedReceiptType(value)}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-lg active:scale-95 ${
                    selected
                      ? `ring-2 ring-${color}-500 border-${color}-500 bg-${color}-50`
                      : 'hover:border-gray-300 border-2 border-gray-200'
                  }`}
                  style={selected ? {
                    borderColor: `var(--color-${color})`,
                    boxShadow: `0 0 0 2px var(--color-${color})`,
                  } : undefined}
                >
                  <CardContent className="p-5 flex flex-col items-center justify-center gap-2 relative">
                    {selected && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-macartur-primary text-white flex items-center justify-center">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                    <Icon className={`h-10 w-10 ${selected ? 'text-macartur-primary' : 'text-gray-500'}`} />
                    <span className={`text-base font-semibold ${selected ? 'text-macartur-primary' : 'text-gray-700'}`}>
                      {label}
                    </span>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Sección 2: Lugar de Consumo */}
        <div className="space-y-3 mt-3">
          <h3 className="text-base font-semibold text-gray-700 border-b pb-2">
            2. Lugar de Consumo
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {diningOptions.map(({ value, label, icon: Icon, color }) => {
              const selected = selectedDiningOption === value;
              return (
                <Card
                  key={value}
                  onClick={() => setSelectedDiningOption(value)}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-lg active:scale-95 ${
                    selected
                      ? `ring-2 ring-macartur-primary border-macartur-primary bg-macartur-primary/5`
                      : 'hover:border-gray-300 border-2 border-gray-200'
                  }`}
                >
                  <CardContent className="p-5 flex flex-col items-center justify-center gap-2 relative">
                    {selected && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-macartur-primary text-white flex items-center justify-center">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                    <Icon className={`h-10 w-10 ${selected ? 'text-macartur-primary' : 'text-gray-500'}`} />
                    <span className={`text-base font-semibold ${selected ? 'text-macartur-primary' : 'text-gray-700'}`}>
                      {label}
                    </span>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
};
